import * as React from 'react';

import { cn } from '@/ux-common/lib/utils';

import { Tooltip, type TooltipPositioningProps, TooltipShell } from './tooltip';

export interface DisabledTooltipProps extends TooltipPositioningProps {
  /**
   * Whether the wrapped control is currently disabled. With `content`, drives the
   * span tab-stop: the wrapper is focusable only when it is both disabled and has
   * something to say, so it never adds a stop that announces nothing.
   */
  isDisabled: boolean;
  /**
   * Tooltip content. When falsy, the tooltip portal is not rendered, but the
   * surrounding wrapper stays mounted so toggling content does not unmount
   * the wrapped control (keyboard focus and effects survive transitions).
   */
  content: React.ReactNode;
  /** Element to wrap. */
  children: React.ReactNode;
  /** Class merged onto the wrapping `<span>`. */
  className?: string;
  /**
   * The wrapper's `data-component` marker — `"DisabledTooltip"` unless you name
   * it. The span is this component's outermost element, so a molecule composed
   * around it whose root IS the span annotates its own component root here.
   */
  'data-component'?: string;
}

/**
 * DisabledTooltip — tooltip wrapper aware of native `disabled` controls.
 *
 * Native `disabled` buttons (and similar elements) swallow pointer events, so
 * attaching a Radix `TooltipTrigger asChild` directly to a disabled element
 * never fires hover/focus. This wrapper inserts a `<span>` between the trigger
 * and the disabled element so the tooltip still opens, and makes the span
 * focusable only while the element is disabled — a permanent `tabIndex={0}`
 * would add a second tab stop beside the enabled control.
 *
 * a11y: Radix wires `aria-describedby` on the trigger (our `<span>`) when the
 * tooltip is open, so AT users tabbing to the disabled-state span hear the
 * description. The span is focusable only when it is disabled *and* has content —
 * with no content there is no tooltip, so a tab stop there would announce nothing. That pairing — a focusable span plus a description Radix can
 * point at — is the whole component; the rest is passthrough.
 *
 * Useful any time a tooltip needs to explain *why* a control is disabled
 * (e.g. form validation, gated actions).
 *
 * Like `withTooltip`, this **mounts no `TooltipProvider`** — consumers render
 * one at the app root. See `with-tooltip.tsx` for the full rationale and the
 * `delayDuration` precedence table.
 *
 * The tooltip half of the prop surface is `TooltipPositioningProps`, shared with
 * `withTooltip` so the two cannot drift apart. Nothing is spread onto a child here,
 * so the prefix is not needed for safety — it matches so the two siblings read as
 * one API rather than two spellings of the same idea.
 *
 * @example
 * ```tsx
 * <DisabledTooltip isDisabled={!canSubmit} content="Fill in every required field" tooltipSide="bottom">
 *   <Button disabled={!canSubmit}>Submit</Button>
 * </DisabledTooltip>
 * ```
 */
export const DisabledTooltip = ({
  isDisabled,
  content,
  children,
  className,
  'data-component': dataComponent = 'DisabledTooltip',
  contentClassName,
  delayDuration,
  tooltipSide,
  tooltipAlign,
  tooltipSideOffset,
  tooltipAlignOffset,
  tooltipArrow,
  interactiveContent,
}: DisabledTooltipProps) => (
  <Tooltip delayDuration={delayDuration}>
    <TooltipShell
      content={content}
      trigger={
        <span
          data-component={dataComponent}
          className={cn('tooltip-disabled-wrapper', className)}
          /*
           * Focusable only when disabled AND carrying content. Enabled, the control
           * is its own tab stop; with no content there is no tooltip, so the stop
           * would announce nothing. See the a11y note in the JSDoc above for why the
           * span exists at all.
           *
           * The rule below is right in general and wrong here: there is no ARIA role
           * for "element that owns a tooltip", and `role="button"` would announce a
           * phantom second button. axe agrees — the a11y suite renders this markup
           * with no violation.
           */
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={isDisabled && content ? 0 : undefined}
        >
          {children}
        </span>
      }
      contentClassName={contentClassName}
      tooltipSide={tooltipSide}
      tooltipAlign={tooltipAlign}
      tooltipSideOffset={tooltipSideOffset}
      tooltipAlignOffset={tooltipAlignOffset}
      tooltipArrow={tooltipArrow}
      interactiveContent={interactiveContent}
    />
  </Tooltip>
);

DisabledTooltip.displayName = 'DisabledTooltip';
